import type { ProjectIcon as ProjectIconName, ProjectIconColor } from '@tms/shared';
import {
  BarChart3,
  Beaker,
  Bot,
  Briefcase,
  Cloud,
  CreditCard,
  Database,
  Folder,
  Globe,
  Layers,
  Lock,
  Monitor,
  Package,
  Server,
  Settings,
  Shield,
  Smartphone,
  Sparkles,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '../../lib/cn';

/** The drawing behind each name of the shared set. */
const DRAWINGS: Record<ProjectIconName, LucideIcon> = {
  folder: Folder,
  layers: Layers,
  globe: Globe,
  smartphone: Smartphone,
  monitor: Monitor,
  server: Server,
  database: Database,
  cloud: Cloud,
  shield: Shield,
  lock: Lock,
  card: CreditCard,
  wallet: Wallet,
  chart: BarChart3,
  bot: Bot,
  package: Package,
  beaker: Beaker,
  briefcase: Briefcase,
  users: Users,
  settings: Settings,
  sparkles: Sparkles,
};

/**
 * The tile the icon sits on: the same token pairs the label chips use, so a
 * project reads in both themes without a palette of its own.
 */
const TILE_STYLES: Record<ProjectIconColor, string> = {
  gray: 'bg-sunken text-ink-muted',
  blue: 'bg-accent-soft text-accent-fg',
  green: 'bg-pass-soft text-pass',
  amber: 'bg-block-soft text-block',
  red: 'bg-fail-soft text-fail',
  purple: 'bg-accent-soft text-accent',
};

export interface ProjectIconProps {
  icon: ProjectIconName;
  color: ProjectIconColor;
  /** `sm` for a list row, `md` beside a heading. */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * A project's icon on its tinted tile. Decorative: every place that shows it
 * also shows the project's key and name, so it carries no label of its own.
 */
export const ProjectIcon = ({ icon, color, size = 'sm', className }: ProjectIconProps) => {
  const Drawing = DRAWINGS[icon];
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md',
        size === 'sm' ? 'size-7' : 'size-9',
        TILE_STYLES[color],
        className,
      )}
    >
      <Drawing className={size === 'sm' ? 'size-4' : 'size-5'} />
    </span>
  );
};
